<template>
  <v-layout column>
    <list-edition-toolbar
      :buttons="toolbarButtons"
      @edit="editionDisabled = false"
      @delete="deleteDialog = true"
      @save="save"
      @cancel="cancel"
    />

    <v-layout row wrap class="task-form-container" v-if="editedTask !== null">
      <input-field
        :label="$t('Title')"
        v-model="editedTask.title"
        variable-name="title"
        input-type="text"
        size="small"
        :field-disabled="editionDisabled"
        required
      />

      <input-field
        :label="$tc('Assignee', 1)"
        :value="editedTask.user_assignee_id"
        variable-name="user_assignee_id"
        input-type="other"
        size="small"
        :field-disabled="editionDisabled"
      >
        <treeselect
          v-model="editedTask.user_assignee_id"
          :options="userOptions"
          :disable-branch-nodes="true"
          value-consists-of="LEAF_PRIORITY"
          disableFuzzyMatching
          :loadOptions="loadAllUsers"
          :disabled="editionDisabled"
          class="form-input-select"
        />
      </input-field>

      <input-field label="" value="" input-type="space" size="small" />

      <input-field
        :label="$t('ScheduledFor')"
        v-model="editedTask.scheduled_date"
        variable-name="scheduled_date"
        input-type="datetime"
        size="small"
        :field-disabled="editionDisabled || task.state === 'closed'"
        :required="task.scheduled_date !== null"
        :min="dateNow"
        :max="activityDate"
      />

      <input-field
        :label="$t('ApprovalType')"
        input-type="other"
        size="small"
        :value="editedTask.auto_approval"
        variable-name="auto_approval"
        :field-disabled="editionDisabled"
      >
        <treeselect
          :value="editedTask.auto_approval"
          :options="approvalType"
          @input="updateTaskApproval($event)"
          class="form-input-select"
          :disabled="editionDisabled"
        />
      </input-field>

      <input-field
        :label="$t('State')"
        :value="task.state"
        variable-name="state"
        input-type="other"
        size="small"
        :field-disabled="true"
      >
        <v-layout row align-center>
          <v-flex shrink>
            <activity-task-status-icon :status="task.state" />
          </v-flex>

          <v-flex class="ml-2" style="color: #acacae">
            <div>{{ getStateLabel(task.state) }}</div>
          </v-flex>
        </v-layout>
      </input-field>

      <input-field label="" value="" input-type="space" size="small" />

      <input-field
        :label="$t('Description')"
        v-model="editedTask.description"
        variable-name="description"
        input-type="textarea"
        size="large"
        :field-disabled="editionDisabled"
      />

      <input-field
        :label="$tc('Dependency', 2)"
        :value="editedTask.dependencies"
        variable-name="dependencies"
        input-type="other"
        size="large"
        :field-disabled="editionDisabled"
      >
        <treeselect
          v-model="editedTask.dependencies"
          :options="dependencyOptions"
          :load-options="loadDependencyOptions"
          :normalizer="dependencyOptionsNormalizer"
          multiple
          :disabled="editionDisabled"
          disableFuzzyMatching
          class="form-input-select"
        />
      </input-field>

      <!-- <input-field
        :label="$tc('Type', 1)"
        :value="editedTask.type"
        variable-name="type"
        input-type="other"
        size="small"
        :field-disabled="true"
      >
        <treeselect
          v-model="editedTask.type"
          :options="taskTypeOptions"
          :disabled="true"
          disableFuzzyMatching
          class="form-input-select"
        />
      </input-field> -->

      <div class="mx-4" v-if="editedTask.type === 'checklist'">
        <task-checklist
          :task="task"
          @addItems="addItem"
          :editionDisabled="editionDisabled || editedTask.state === 'completed'"
          @editItems="editItem"
          @removeItem="removeItem"
        />
        <v-alert
          :value="disableSave"
          type="error"
          :outlined="true"
          transition="scale-transition"
          class="mt-3"
        >
          {{ $t("addItemAlert") }}
        </v-alert>
      </div>
      <div
        class="mx-4 ma-4"
        v-if="editedTask.type === 'photo' && editionDisabled"
      >
        <photo-view-edit-dialog :task="editedTask.content" :width="width" />
      </div>
      <div
        class="mx-4 ma-4"
        v-if="editedTask.type === 'photo' && !editionDisabled"
      >
        <photo
          :task="editedTask"
          @removeItem="removeItem"
          @addItems="addItem"
          :editionDisabled="editionDisabled || editedTask.state === 'completed'"
          @editItems="editItem"
        />
        <v-alert
          :value="disableSave"
          type="error"
          :outlined="true"
          transition="scale-transition"
          class="mt-3"
        >
          {{ $t("addItemAlert") }}
        </v-alert>
      </div>
      <div
        class="mx-4 ma-4"
        v-if="editedTask.type === 'mixed'"
      >
        <Mixed
          :task="editedTask"
          @addItems="addItem"
          @editItems="editItem"
          :editionDisabled="editionDisabled || editedTask.state === 'completed'"
          @removeItem="removeItem"
        />
        <v-alert
          :value="disableSave"
          type="error"
          :outlined="true"
          transition="scale-transition"
          class="mt-3"
        >
          {{ $t("addItemAlert") }}
        </v-alert>
      </div>
      <div class="mx-auto" v-if="editedTask.type === 'text'">
        <content-text
          :task="editedTask"
          @removeItem="removeItem"
          @addItems="addItem"
          :editionDisabled="editionDisabled || editedTask.state === 'completed'"
          @editItems="editItem"
        />
        <v-alert
          :value="disableSave"
          type="error"
          :outlined="true"
          transition="scale-transition"
          class="mt-3"
        >
          {{ $t("addItemAlert") }}
        </v-alert>
      </div>
    </v-layout>

    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteItem')"
      :content="$t('DeleteConfirmation') + ' ' + task.title + '?'"
      :main-action-label="$t('No')"
      @mainAction="deleteDialog = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="remove"
    />
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";
import InputField from "@/components/Home/Forms/_Molecules/InputField";
import ListEditionToolbar from "@/components/Home/Forms/_Molecules/ListEditionToolbar";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import ActivityTaskStatusIcon from "../../../../../FieldManagement/StatusIcon";
import _ from "lodash";
import PhotoViewEditDialog from "../TaskTypes/Photos/PhotoViewEditDialog";
import TaskChecklist from "../TaskTypes/Checklists/Checklist";
import { activitiesTasksDataMixin } from "@/mixins/field/activitiesTasksDataMixin";
import ContentText from "../TaskTypes/Text/ContentText.vue";
import Photo from "../TaskTypes/Photos/Photo.vue";
import Mixed from "../TaskTypes/Mixed/Mixed.vue";

export default {
  name: "ObjectFormTaskDetailsForm",

  mixins: [activitiesTasksDataMixin],

  components: {
    ActivityTaskStatusIcon,
    AppDialogBox,
    ListEditionToolbar,
    InputField,
    TaskChecklist,
    PhotoViewEditDialog,
    ContentText,
    Photo,
    Mixed
  },

  props: {
    task: {
      type: Object,
      required: true
    },
    activityUUID: String,
    actionType: String,
    width: Number
  },

  data() {
    return {
      editionDisabled: true,
      loading: false,
      editedTask: null,
      tasks: undefined,
      dependencyOptions: null,
      deleteDialog: false
    };
  },
  /**
   * when component is loaded, loads all users (ref to activitiesandtasksmixin)
   */
  created() {
    this.loadAllUsers();
  },

  computed: {
    /**
     * list of approval types
     */
    approvalType() {
      return [
        {
          label: this.$i18n.t("AutoApproved"),
          id: true
        },
        {
          label: this.$i18n.t("ManualApproval"),
          id: false
        }
      ];
    },
    /**
     * Button configurations loaded in listToolBar component.
     */
    toolbarButtons() {
      return [
        {
          id: "edit",
          icon: "edit",
          tooltip: this.$i18n.t("Edit"),
          active: this.editionDisabled,
          disabled: this.actionType === "view"
        },
        {
          id: "cancel",
          icon: "cancel",
          tooltip: this.$i18n.t("Cancel"),
          active: !this.editionDisabled,
          disabled: this.disableSave
        },
        {
          id: "save",
          icon: "save",
          tooltip: this.$i18n.t("Save"),
          active: !this.editionDisabled,
          disabled: this.disableSave
        },
        {
          id: "delete",
          icon: "delete",
          tooltip: this.$i18n.t("Delete"),
          active: this.editionDisabled,
          disabled: this.actionType === "view"
        }
      ];
    },
    /**
     * Disables Save button if content is not filled.
     */
    disableSave() {
      return this.task.content.length <= 0;
    },
    /**
     * Loads user options in treeSelect creating 2 branches with children nodes based on type (Web and Mobile)
     */
    userOptions() {
      let web = [];
      let mobile = [];

      this.allUsers.forEach(user => {
        if (user.type === "web") {
          web.push({
            id: user.id,
            label: user.first_name + " " + user.last_name,
            type: user.type
          });
        } else {
          mobile.push({
            id: user.id,
            label: user.first_name + " " + user.last_name,
            type: user.type
          });
        }
      });
      return [
        {
          id: "Web",
          label: this.$t("Web"),
          children: web
        },
        {
          id: "Mobile",
          label: this.$t("Mobile"),
          children: mobile
        }
      ];
    }
  },
  /**
   * watches changes in task data, assigns data into editedTask variable.
   */
  watch: {
    task() {
      this.setFormData();
    }
  },
  /**
   * creates clone of original task
   * sets task data in a new variable this.editedTask
   */
  mounted() {
    this.tasks = _.cloneDeep(this.task);
    this.setFormData();
  },

  methods: {
    ...mapActions([
      "updateTask",
      "deleteTask",
      "updateTaskContent",
      "createTaskContent",
      "deleteTaskContent"
    ]),

    // loadDependencyOptions({ callback }) {
    //   //TODO Implement loadDependencyOptions final version
    //   callback();
    // },

    // dependencyOptionsNormalizer(node) {
    //   return {
    //     id: node.id,
    //     label: node.id + ": " + node.title
    //   };
    // },

    /**
     * @param {Object} item - new added content
     *
     * adds a new item into content array (with no uuid)
     */
    addItem(item) {
      this.editedTask.content.push(item);
    },
    /**
     * @param {Object} editedItem - edited properties
     * @param {Number} itemIndex - index of edited Map
     *
     * receives editedItem and replaces the item using the index.
     */
    editItem(editedItem, itemIndex) {
      this.editedTask.content.splice(itemIndex, 1, editedItem);
    },
    /**
     * @param {number} index - index of task to delete
     * @param {string} uuid - receives task content uuid
     *
     * if task has uuid, a request is made to remove that content from the task.
     * also removes content from table with or without uuid.
     * (updateTask does not support the deletion of content)
     */
    async removeItem(index, uuid) {
      if (uuid) {
        try {
          await this.deleteTaskContent({
            taskUUID: this.editedTask.uuid,
            contentUUID: uuid
          });
        } catch (error) {
          console.log(error);
        }
      }
      this.editedTask.content.splice(index, 1);
    },
    /**
     * Assigns received task prop into new variable.
     */
    setFormData() {
      this.editedTask = Object.assign({}, this.task);
      this.editedTask.type = "mixed";
    },
    /**
     * disables edition.
     */
    stopEditing() {
      this.editionDisabled = true;
    },
    /**
     * sets unedited FormData and disables editing.
     */
    cancel() {
      this.setFormData();
      this.stopEditing();
    },
    /**
     * If there are changes in task properties, update task executes.
     * If task has content with no uuid, request to create executes.
     * else if content label is different from original, updateTaskContent executes.
     * Stops edition mode
     * and returns back into view mode of task details.
     */
    async save() {
      // const updatedData = _.omitBy(
      //   this.editedTask,
      //   (value, key) => this.task[key] === value
      // );

      const updatedData = this.task.content

      try {
        this.loading = true;
        if (updatedData && Object.keys(updatedData).length !== 0) {
          await this.updateTask({
            taskUUID: this.task.uuid,
            updatedData
          });
        }

        for (const editedContent of this.editedTask.content) {
          if (!editedContent.uuid) {
            await this.createTaskContent({
              taskUUID: this.task.uuid,
              updatedData: editedContent
            });
          } else if (
            !this.tasks.content.find(item => item.label === editedContent.label) ||
            !this.tasks.content.find(item => item.index === editedContent.index) ||
            !this.tasks.content.find(item => item.required === editedContent.required)
          ) {
            await this.updateTaskContent({
              taskUUID: this.task.uuid,
              contentUUID: editedContent.uuid,
              updatedData: editedContent
            });
          }
        }
        this.stopEditing();
        this.$emit("update");
      } catch (e) {
        console.error(e);
        this.$toasted.global.toast_error(e.response.data.message);
        this.loading = false;
      }
    },
    /**
     * updates task approval state
     */
    updateTaskApproval(value) {
      this.editedTask.auto_approval = value;
    },

    /**
     * Deletes selected task.
     */
    async remove() {
      try {
        this.loading = true;
        await this.deleteTask({
          activityUUID: this.activityUUID,
          taskUUID: this.task.uuid
        });
        this.$emit("delete");
      } catch (e) {
        console.error(e);
        this.$toasted.global.toast_error(e.response.data.message);
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.task-form-container {
  border-radius: 2px;
  box-shadow: -0.5px 0.5px 2px rgba(0, 0, 0, 0.5);
  padding-top: 10px;
  padding-bottom: 20px;
  margin-bottom: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Activities/TaskDetails/TaskDetailsForm.vue